<script setup lang="ts">
/**
 * /pricing-tables —— 计价表(v2 · O5 按画板重排,深色;S-46)。
 *
 * 人在这里要能做完整条链路(S-02 那版的能力一件不丢):
 *   ① 编一版计价表草稿(重量档 × 体积/规格档 × 距离档 × 超长加价,锚地内外各一套)→ 新建一版
 *   ② 在右边点中那一版,发布它 —— 上一版自动进归档
 *
 * 页面上刻意显示两件平时看不见的事:
 *   · 人按友好单位输入(重量 kg / 体积 m³ / 金额 元,Y-59),提交时精确换成
 *     整数克 / 立方厘米 / 分 —— 库里与契约仍是 M-14 的最小单位
 *   · 命中的是锚地内还是锚地外那套表 —— 判错了金额会差一大截,但它不会报错
 *
 * ═══ 这里原来还有一个「试算一单」面板,2026-09-22 按 N-07 撤掉,不许接回来 ═══
 *
 * 它调的 POST /api/quotes 的受众是小程序端(S-03 的裁决:匿名试算能被拿去批量反推
 * 整张价目表),运营端拿着 ops 令牌去调必然 401,而客户端的统一 401 处理会清掉令牌
 * 跳登录 —— 也就是说那个面板从来没有成功发出去过一次,运营点一下的体验是「我被退出登录了」。
 * N-07 拍板:不补接口,要核对价格就看订单详情里成交当时写下的快照(M-11)——
 * 那比「拿今天的表算一遍」更准,它记的是当时生效的那张表。
 * 重开的触发器:真的需要运营端试算时,先落一条决策 + 在契约里加一条运营端受众的接口;
 * **不许**把面板接回 /api/quotes,也不许放宽那条接口的受众。
 *
 * ═══ 与画板的差异(都记在 design-copy 的 O5 帧里)═══
 *
 * ① **画板上的档位数字(≤ 5 kg / ¥30.00 那些)不进实现**:契约没有矩阵读回口
 *    (GET 只回 version / status / publishedAt / createdAt),画板上那些数是 seed
 *    播种的演示价 —— 照抄进页面只能是硬编码,与 M-02「计价表不写进代码」冲突。
 *    左栏因此是「新一版草稿的输入」:**档位是每档一行的两格输入**(上界按
 *    kg / 公里 / m³,金额按元 —— Y-59),数字是**可改的输入初值**
 *    (BLK-01 的占位假数据,外置在 ./pricing-draft-defaults.json),不是读回来的在售价 ——
 *    「发布中的那一版现在是多少」这一页答不出来(没有读回口),不许装作答得出。
 *
 *    ── 行编辑器的三条约定(改这一块之前先读)──────────────
 *    · **下界只读、由上一档自动推出**:契约里没有下界这一栏,放开编辑等于允许造出空档;
 *      空档不会报错,那一段货会被静默算进上一档。区间列因此是显示用的推导,不是第二套命中逻辑。
 *    · **上界乱序标红、不自动排序**:排序会改变哪笔钱对应哪段货,而页面上看不出任何异常。
 *    · **单位换算仍在本文件**:TierRowsEditor 不认识 kg / 元 / m³,它拿到的是两串文本。
 *      共享包那几个精确十进制解析函数只住在这里(M-14 / Y-59 的口径因此只有一个源);
 *      两条既有守卫按文件名登记了它们,搬进组件会让守卫变红。
 * ② 画板只画了重量 × 距离两维:**第三维(体积/规格档)与超长规格加价照契约保留**
 *    (契约必填,简化不是删功能的许可)。它们的输入跟在距离档后面。
 * ③ 画板没有「新建一版」入口(直接从编辑态跳到「发布 v3」)—— 它是补件:
 *    POST /api/admin/pricing-tables 没有落点的话,建草稿这条链在界面上就是死的。
 * ④ 版本行的选中态与行内两行小字(创建时间 / 发布时间)是画板没画的补件:
 *    画板把版本号画死在按钮上(「发布 v3」),而按钮要知道发的是哪一版 ——
 *    改成「点一行选中它,按钮跟着变」。
 * ⑤ 画板版本行状态标签的词是「未发布 / 已下线」,而单点表(labels.ts 的
 *    PRICING_TABLE_STATUS_LABELS)里是「草稿 / 已归档」—— **以单点表为准**,
 *    画板那两个词登记在帧的豁免里(改表词是一条跨端文案决策,不在本切片)。
 * ⑥ 画板那行改版自述(「不再是一排等宽输入框……」)不落进页面:它说的是画板自己
 *    对上一版的变化,不是给运营的说明;档位是每档一行的两格输入(见上面那三条约定)。
 */
import { computed, nextTick, onMounted, reactive, ref } from 'vue'
import { NButton, NInput, NInputNumber, NSpace, useMessage } from 'naive-ui'
import {
  ErrorBanner,
  PageHeader,
  PanelCard,
  StatusTag,
  anchorageScopeLabel,
  pricingTableStatusGroup,
  pricingTableStatusLabel,
  type ScopeNote,
} from '@sea-drone/ui'
import {
  ANCHORAGE_SCOPE,
  formatShanghaiDateTime,
  parseKgToGrams,
  parseM3ToCm3,
  parseScaledDecimal,
  parseWholeNumber,
  parseYuanToFen,
} from '@sea-drone/shared'
import { api, ApiError, type PricingTable } from '../api/client'
import TierRowsEditor, { type TierRow, type TierRowError } from '../components/TierRowsEditor.vue'
import defaultDraft from './pricing-draft-defaults.json'

/** 这一页是运营端的深色页,底座由 S-41 的第三档 tone 给(AppShell 上已挂同一档) */
const TONE = 'ops-dark' as const

const message = useMessage()

const tables = ref<PricingTable[]>([])
const lastError = ref<string | null>(null)

/** 选中要发布的那一版 —— 画板把版本号画死在发布按钮上,这里改成点一行选中它 */
const selectedVersion = ref<string | null>(null)

const selectedTable = computed(() => tables.value.find((t) => t.version === selectedVersion.value) ?? null)
/** 只有草稿能发布 —— 发布中 / 已归档的版本没有可发布的动作(服务端也会拒) */
const publishTarget = computed(() => (selectedTable.value?.status === 'draft' ? selectedTable.value : null))

/**
 * 草稿的初值是占位假数据(BLK-01 的真表还没到),**外置在 ./pricing-draft-defaults.json** ——
 * 单独成文件是为了让守卫能直接读它做「默认档位逐值不变」的断言,同时把这批常量从视图里挪出去。
 * 档位的**结构**是真的:三维分档(重量 × 体积/规格 × 距离,锚地内外两套)——
 * 0-5kg / 5-10kg / 10-30kg,10/20/30/50 公里,体积档默认一档盖住所有单、金额 0
 * (所以默认表下算出来的钱与加这一维之前完全相同)。
 *
 * 每格是字符串表单态,提交时才解析:人输入与存储口径的边界只在一处(下面 checkTiers 与
 * createDraft 用的共享包解析函数),中途不出现浮点(M-14、Y-59)。
 */
function toRows(pairs: readonly (readonly string[])[]): TierRow[] {
  return pairs.map(([bound, amount]) => ({ bound: bound ?? '', amount: amount ?? '' }))
}

const draft = reactive({
  version: `${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-v1`,
  insideWeight: toRows(defaultDraft.insideWeight),
  insideDistance: toRows(defaultDraft.insideDistance),
  insideVolume: toRows(defaultDraft.insideVolume),
  insideOversized: defaultDraft.insideOversized,
  outsideWeight: toRows(defaultDraft.outsideWeight),
  outsideDistance: toRows(defaultDraft.outsideDistance),
  outsideVolume: toRows(defaultDraft.outsideVolume),
  outsideOversized: defaultDraft.outsideOversized,
  centerLat: defaultDraft.centerLat,
  centerLng: defaultDraft.centerLng,
  // S-61:半径是字符串表单态。用 n-input-number 的 :precision="0" 会把 12.5
  // 静默四舍五入成 13,运营以为自己填的是 12.5,库里却是 13。
  radiusKm: defaultDraft.radiusKm,
})

/** 分段控件的选中态是客户端状态:两套矩阵同在一版计价表里,一次编一套 */
const scope = ref<(typeof ANCHORAGE_SCOPE)[number]>(ANCHORAGE_SCOPE[0])

const NOTES: readonly ScopeNote[] = [
  {
    title: '这一页能做什么',
    body:
      '编一版三维计价表(重量档 × 体积/规格档 × 距离档,锚地内外各一套)→ 新建一版草稿 → ' +
      '在右边点中它、发布,上一版自动进归档。',
    refs: ['S-02'],
  },
  {
    title: '这里填的数字是占位假数据',
    body:
      '数字是可改的输入初值、占位假数据(甲方的真实价目表还没到,真表到货那天只发布一版新计价表,' +
      '一行代码都不用改)。金额一律填「元」,最多 2 位小数 —— 120 元就填 120;' +
      '重量上界填 kg(最多 3 位小数)、体积上界填 m³(最多 6 位小数)、距离上界填整数公里。' +
      '**这一页读不回「发布中那一版现在是多少」**:契约的版本列表只有' +
      '版本号 / 状态 / 发布时间 / 创建时间,没有矩阵读回口 —— 要知道在售价,走订单详情里' +
      '成交当时的快照(M-11),那比这一页准。',
    refs: ['BLK-01', 'M-11'],
  },
  {
    title: '金额一律以「元」输入',
    body:
      '金额一律以「元」输入、最多 2 位小数;提交时精确换成整数分(M-14 的存储口径不变)。' +
      '多填一位小数会被当场拒绝,不会四舍五入 —— 静默抹掉的那一位在钱上无人看得见。',
    refs: ['M-14', 'Y-59'],
  },
  {
    title: '体积/规格档先给默认值',
    body:
      '第三维(体积/规格档)的默认值是一档盖住所有单、金额 0 —— 这样它一分钱都不加,' +
      '与只有重量 × 距离两维时完全相同。填成多档之后:订单按体积(内部是立方厘米,这里填 m³)命中第一个' +
      '上界足够的档,档位金额 > 0 才在明细里出一行;没有量过体积的订单按最小档命中。' +
      '它和「超长规格加价」并存,真表按体积分档时把超长加价填 0。',
    refs: ['M-02'],
  },
  {
    title: '命中的是锚地内还是锚地外',
    body:
      '命中的是锚地内还是锚地外那套表 —— 判错了金额会差一大截,但它不会报错。' +
      '起终点任一落在圆外,整单按锚地外计价(AS-009 的假设,真边界是海图上的多边形)。' +
      '上面那个分段控件只是在两套输入之间切换,两套都要填 —— 它们同在一版里。',
    refs: ['AS-009'],
  },
]

/** 挂在「版本」那张卡上 —— 「不删」那句话从表格单元格里搬到这里 */
const LIST_NOTES: readonly ScopeNote[] = [
  {
    title: '归档版本不删',
    body:
      '同一时刻只有一版「发布中」。发布新版之后上一版进归档 —— 不删,快照要用:' +
      '历史订单的快照里存着它的版本号,删了那些订单就没有依据可查了。',
    refs: ['S-02'],
  },
  {
    title: '发布按钮发的是哪一版',
    body:
      '点上面任一行选中它,按钮跟着变成「发布 版本号」。只有草稿能发布 —— ' +
      '发布中那一版已经在生效、归档那一版是历史,它们没有可发布的动作。',
    refs: [],
  },
]

function show(e: unknown): void {
  const msg =
    e instanceof ApiError
      ? `[${e.code}] ${e.message}(requestId ${e.requestId})`
      : e instanceof Error
        ? e.message
        : String(e)
  lastError.value = msg
  message.error(msg, { duration: 8000 })
}

/** 六张档位表 —— 键同时是 draft / tierErrors 的字段名 */
type TierKey =
  | 'insideWeight'
  | 'insideDistance'
  | 'insideVolume'
  | 'outsideWeight'
  | 'outsideDistance'
  | 'outsideVolume'

type TierSide = (typeof ANCHORAGE_SCOPE)[number]

/** 契约里每档的三个可能的上界键(一次只用其中一个) */
type BoundKey = 'maxGram' | 'maxKm' | 'maxVolumeCm3'
type BoundParser = (s: string) => number | null

interface TierCheck {
  values: { maxGram?: number; maxKm?: number; maxVolumeCm3?: number; amount: number }[]
  errors: Record<number, TierRowError>
  firstError: { index: number; field: TierRowError['field']; message: string } | null
}

interface TierTableMeta {
  key: TierKey
  /** 上界解析键 —— 契约里的字段名,同时是 checkTiers 的入参 */
  boundKey: BoundKey
  /** 这张表属于锚地内还是外 —— 分区段控件用 */
  side: TierSide
  /** 报错文案里的维度名,与表头同词 */
  label: string
}

/**
 * 六张表的遍历顺序(= 页面上从上到下的顺序)、上界解析键、侧别与维度名。
 * 只列一张表、不再维护几张并行映射:并行映射少写一行不会报错,而报错文案会连环错。
 */
const TIER_TABLES: readonly TierTableMeta[] = [
  { key: 'insideWeight', boundKey: 'maxGram', side: 'inside', label: '重量档' },
  { key: 'insideDistance', boundKey: 'maxKm', side: 'inside', label: '距离档' },
  { key: 'insideVolume', boundKey: 'maxVolumeCm3', side: 'inside', label: '体积档' },
  { key: 'outsideWeight', boundKey: 'maxGram', side: 'outside', label: '重量档' },
  { key: 'outsideDistance', boundKey: 'maxKm', side: 'outside', label: '距离档' },
  { key: 'outsideVolume', boundKey: 'maxVolumeCm3', side: 'outside', label: '体积档' },
]

/**
 * 按表名取元信息。TIER_TABLES 在类型上覆盖全部六个键,取不到只可能是这张表被漏登记 ——
 * 那时抛出来,不猜一个默认的上界口径(猜出来的校验会静默少查一维)。
 */
function metaOf(key: TierKey): TierTableMeta {
  const found = TIER_TABLES.find((table) => table.key === key)
  if (found === undefined) throw new Error(`内部错误:${key} 不在 TIER_TABLES 里`)
  return found
}

/**
 * 上界按这一维的单位解析(kg / 公里 / m³),金额一律元。
 * 解析不了就报错,**不猜**:猜出来的档位会静默算错钱,而页面上看不出任何异常。
 *
 * 金额:元,最多 2 位小数(parseYuanToFen,多一位当场拒绝、不四舍五入)。
 * 重量:kg,最多 3 位小数。体积:m³,最多 6 位小数。距离:整数公里,最多 0 位小数。
 */
const BOUND_UNITS: Record<BoundKey, { parse: BoundParser; message: string }> = {
  maxGram: { parse: parseKgToGrams, message: '上界要填非负的公斤数,最多 3 位小数' },
  maxKm: { parse: (s: string) => parseScaledDecimal(s, 0), message: '上界要填非负整数公里' },
  maxVolumeCm3: { parse: parseM3ToCm3, message: '上界要填非负的立方米数,最多 6 位小数' },
}

const AMOUNT_MESSAGE = '金额要填非负的元数,最多 2 位小数;多出来的那位不会被四舍五入'

/**
 * 逐格校验一维档位表。**不抛异常、不猜、不排序** —— 返回三样东西:
 *   values     校验通过的档位(有错时只用于展示,不会拿它提交)
 *   errors     按行下标的红格(每行最多一条;先报「哪一格还空着」,
 *              再是上界的解析与顺序,金额的解析排最后)
 *   firstError 行序第一个错,提交前用它切分段控件并把光标送过去
 *
 * 规则(按行序):
 *   ① 两格都空 → 整行忽略,连区间列都显示「未填」(新增出来的空行就是这样)
 *   ② 只有一格空 → 该格报错,prev 不动
 *   ③ 上界解析不了 → 报错,prev 不动
 *   ④ 上界 ≤ prev → 报错「必须大于上一档」,**不排序**:排序会改变哪笔钱对应哪段货
 *   ⑤ 金额解析不了 → 报错,但 prev 更新为这一档的上界(它自己合法)
 *   ⑥ 正常 → 收进 values,prev 跟着走
 *
 * ② 排在 ③④⑤ 前面是有意的:一格空着时先报「还没填」—— 那是人正在做的事,
 * 而拿空串去解析只会多出一条看不懂的格式错。④ 排在 ⑤ 前面同样是有意的:一行里
 * 两个错都要报时,上界那条才是根 —— 它决定哪段货进哪一档。而且只有顺序合法的上界
 * 才算「合法的上一档」,prev 才不会被一个乱序值带偏,把后面本来合法的行误标红。
 */
function checkTiers(rows: readonly TierRow[], boundKey: BoundKey): TierCheck {
  const unit = BOUND_UNITS[boundKey]
  const values: TierCheck['values'] = []
  const errors: Record<number, TierRowError> = {}
  let firstError: TierCheck['firstError'] = null
  /** 上一档通过校验的上界(值 + 它原本的文本,报错时要原样引回来) */
  let prev: { value: number; text: string } | null = null

  const fail = (index: number, field: TierRowError['field'], message: string): void => {
    errors[index] = { field, message }
    if (firstError === null) firstError = { index, field, message }
  }

  rows.forEach((row, index) => {
    const boundText = row.bound.trim()
    const amountText = row.amount.trim()
    if (boundText === '' && amountText === '') return
    if (boundText === '') {
      fail(index, 'bound', '上界还没填')
      return
    }
    if (amountText === '') {
      fail(index, 'amount', '金额还没填')
      return
    }

    const bound = unit.parse(boundText)
    if (bound === null) {
      fail(index, 'bound', unit.message)
      return
    }

    if (prev !== null && bound <= prev.value) {
      fail(index, 'bound', `上界必须大于上一档(${prev.text})`)
      return
    }

    const amount = parseYuanToFen(amountText)
    if (amount === null) {
      fail(index, 'amount', AMOUNT_MESSAGE)
      prev = { value: bound, text: boundText }
      return
    }

    // 键序与改造前一致(上界在前、金额在后):请求体逐字比对时键序也算 diff
    values.push({ [boundKey]: bound, amount })
    prev = { value: bound, text: boundText }
  })

  return { values, errors, firstError }
}

/**
 * 红格状态。键是表名,值是「行下标 → 红格」。
 * 失焦与输入时只重建一张表;提交时六张一起重建 —— 三条路径共用 checkTiers 一个判定。
 */
const tierErrors = reactive<Record<TierKey, Record<number, TierRowError>>>({
  insideWeight: {},
  insideDistance: {},
  insideVolume: {},
  outsideWeight: {},
  outsideDistance: {},
  outsideVolume: {},
})

/** 整体替换一张表的红格(重建而不是就地增删:上标号变了也不会留下孤儿键) */
function applyErrors(key: TierKey, errors: Record<number, TierRowError>): void {
  tierErrors[key] = { ...errors }
}

/** 失焦:整张表重跑一次 —— 已经在红的格子继续输入时,下一次 change 靠它消红 */
function onTierBlur(key: TierKey): void {
  applyErrors(key, checkTiers(draft[key], metaOf(key).boundKey).errors)
}

/**
 * 改一格:先写回草稿,再决定要不要重算红格。
 * 表本来就是干净的时候不重算 —— 人正在打第一个字就描红,比错到最后才说更烦;
 * 表已经红着的时候实时重算,改对即刻消红。
 */
function onTierChange(
  key: TierKey,
  index: number,
  field: TierRowError['field'],
  value: string,
): void {
  const row = draft[key][index]
  if (row === undefined) return
  row[field] = value
  if (Object.keys(tierErrors[key]).length > 0) {
    applyErrors(key, checkTiers(draft[key], metaOf(key).boundKey).errors)
  }
}

/**
 * 删一档。红格的键是**行下标**:前面删掉一行,原来的红格就落到了没毛病的行上 ——
 * 所以表里本来有错时必须立刻按新下标重算一遍。表干净时跳过(与 onTierChange 同一条规则)。
 */
function removeTier(key: TierKey, index: number): void {
  draft[key].splice(index, 1)
  if (Object.keys(tierErrors[key]).length > 0) {
    applyErrors(key, checkTiers(draft[key], metaOf(key).boundKey).errors)
  }
}

/**
 * 六张表的组件实例。只用来聚焦,不参与渲染,所以是普通对象不是 reactive ——
 * 它每次渲染都会被 Vue 重新赋一遍,做成响应式只会徒增重渲染。
 *
 * 聚焦是**跨侧的**:报错的表可能不在当前这一侧(分段控件用 v-if 只渲染一侧),
 * 此时实例是 null,提交前必须先切 scope、等下一轮渲染再聚焦,否则光标静默留在原处。
 */
const editors: Record<
  TierKey,
  { focusCell: (index: number, field: TierRowError['field']) => Promise<void> } | null
> = {
  insideWeight: null,
  insideDistance: null,
  insideVolume: null,
  outsideWeight: null,
  outsideDistance: null,
  outsideVolume: null,
}

/**
 * 加一档:追加一行空的,然后把光标送进新行的上界格(设计规格 §2:不用人再点一下)。
 * 新行要等一轮渲染才存在 —— 组件里的 focusCell 自己会等,这里不重复 nextTick。
 */
function addTier(key: TierKey): void {
  draft[key].push({ bound: '', amount: '' })
  void editors[key]?.focusCell(draft[key].length - 1, 'bound')
}

function setEditor(key: TierKey, el: unknown): void {
  editors[key] =
    (el as { focusCell: (index: number, field: TierRowError['field']) => Promise<void> } | null) ?? null
}

/** 超长规格加价:人填元,提交前换成整数分。转不了就抛,不许当 0 */
function parseOversizedYuan(input: string): number {
  const fen = parseYuanToFen(input)
  if (fen === null) {
    throw new Error(`超长规格加价看不懂:要「元」为单位的非负数,最多 2 位小数:${input}`)
  }
  return fen
}

async function refresh(): Promise<void> {
  try {
    const page = await api.listPricingTables()
    tables.value = page.items
    // 选中的那一版没了(换了环境 / 刚发布的列表刷新)—— 落到第一版草稿上,
    // 否则按钮会永远停在「发布」二字上,看起来像坏了
    if (!page.items.some((t) => t.version === selectedVersion.value)) {
      selectedVersion.value = page.items.find((t) => t.status === 'draft')?.version ?? null
    }
    lastError.value = null
  } catch (e) {
    show(e)
  }
}

/** 选中一行 —— 点已选中的那一行就取消选中 */
function select(t: PricingTable): void {
  selectedVersion.value = selectedVersion.value === t.version ? null : t.version
}

async function createDraft(): Promise<void> {
  // ① 六张表各跑一次逐格校验,红格写回组件(与失焦时同一条判定,见 checkTiers)。
  // 维度 → 上界解析键的映射只在 TIER_TABLES 里写一次,这里跟着它遍历
  const checks = {} as Record<TierKey, TierCheck>
  for (const table of TIER_TABLES) {
    checks[table.key] = checkTiers(draft[table.key], table.boundKey)
    applyErrors(table.key, checks[table.key].errors)
  }

  // ② 行序第一个错:切到它所在的那一侧、等渲染、把光标送过去,然后停在这里。
  // 六张表的红格在 ① 已经全部标出来了 —— 人改完第一处再点一次,不会又冒出一处新的。
  for (const table of TIER_TABLES) {
    const first = checks[table.key].firstError
    if (first === null) continue
    if (scope.value !== table.side) {
      // 报错的表在另一侧时它还没渲染(v-if),不切 scope 就是 focusCell 拿到 null:
      // 光标静默留在原处,而报错条照样出现 —— 人以为堵住了,其实没指到地方
      scope.value = table.side
      await nextTick()
    }
    await editors[table.key]?.focusCell(first.index, first.field)
    show(
      new Error(
        `${anchorageScopeLabel(scope.value)} ${table.label} 第 ${first.index + 1} 档:${first.message}`,
      ),
    )
    return
  }

  // ③ 没有红格:照旧拼 payload(单位换算只发生在这里,组件不认识单位)
  let input
  try {
    // S-61:整数公里,解析不了就拒 —— 不 clamp、不 Math.round、不 `?? 1`。
    // 四舍五入会把「12.5」变成 13,而 12.5 恰好可能是运营真想要的边界。
    const radiusKm = parseWholeNumber(draft.radiusKm)
    if (radiusKm === null || radiusKm < 1) {
      throw new Error('锚地半径必须填整数公里,1 公里起;12.5 不会被四舍五入成 13')
    }
    // 空行在 checkTiers 里被整行忽略 —— 六张表都空(人把格子全清掉)时这里拦下:
    // 一张没有档位的计价表算不出任何一单,不许发一个空数组让服务端去拒
    for (const table of TIER_TABLES) {
      if (checks[table.key].values.length > 0) continue
      // 空表可能停在另一侧:没有格子可聚焦,但至少要把那张表露出来,
      // 否则人看到「锚地外 距离档 至少要有一档」而眼前是锚地内
      if (scope.value !== table.side) {
        scope.value = table.side
        await nextTick()
      }
      throw new Error(
        `${anchorageScopeLabel(table.side)} ${table.label} 至少要有一档 —— 一张没有档位的计价表算不出任何一单`,
      )
    }
    input = {
      version: draft.version.trim(),
      anchorageInside: {
        weightTiers: checks.insideWeight.values as { maxGram: number; amount: number }[],
        distanceTiers: checks.insideDistance.values as { maxKm: number; amount: number }[],
        volumeTiers: checks.insideVolume.values as { maxVolumeCm3: number; amount: number }[],
        oversizedSurcharge: parseOversizedYuan(draft.insideOversized),
      },
      anchorageOutside: {
        weightTiers: checks.outsideWeight.values as { maxGram: number; amount: number }[],
        distanceTiers: checks.outsideDistance.values as { maxKm: number; amount: number }[],
        volumeTiers: checks.outsideVolume.values as { maxVolumeCm3: number; amount: number }[],
        oversizedSurcharge: parseOversizedYuan(draft.outsideOversized),
      },
      anchorageGeofence: {
        centerLat: draft.centerLat,
        centerLng: draft.centerLng,
        radiusKm,
      },
    }
  } catch (e) {
    show(e)
    return
  }
  try {
    const created = await api.createPricingTable(input)
    message.success(`草稿 ${created.version} 已建 —— 还没生效,下单只读发布中的那一版`)
    await refresh()
  } catch (e) {
    show(e)
  }
}

async function publishSelected(): Promise<void> {
  const target = publishTarget.value
  if (target === null) {
    message.warning('先在右边点中一版草稿 —— 只有草稿能发布')
    return
  }
  try {
    await api.publishPricingTable(target.version)
    message.success(`${target.version} 已发布;上一版自动进归档(不删 —— 历史订单快照里存着它的版本号)`)
    await refresh()
  } catch (e) {
    show(e)
  }
}

onMounted(() => {
  void refresh()
})
</script>

<template>
  <div class="page">
    <PageHeader :tone="TONE" title="计价表" :notes="NOTES">
      <template #actions>
        <!-- 画板页头右端那一行:先说明(逐字)、再刷新(幽灵键;这一板的刷新没有图标) -->
        <span class="header-note">改价是建新一版并发布,不改旧版</span>
        <button type="button" class="ghost" @click="refresh">刷新</button>
      </template>
    </PageHeader>

    <ErrorBanner v-if="lastError" :tone="TONE" :message="lastError" title="上一次操作失败" />

    <div class="panels">
      <!-- 左栏:新一版草稿的输入(画板上的档位数字是 seed 演示值,不进实现 —— 见文件头 ①) -->
      <PanelCard :tone="TONE" title="档位编辑">
        <n-space align="center" class="row">
          <span class="field-label">版本号</span>
          <n-input v-model:value="draft.version" class="version-input" placeholder="2026Q4-v1" />
          <n-button type="primary" size="small" @click="createDraft">新建一版</n-button>
        </n-space>

        <!-- 分段控件:一次编一套矩阵(两套同在一版里,切换只是换输入的面) -->
        <div class="segment" role="tablist">
          <button
            v-for="s in ANCHORAGE_SCOPE"
            :key="s"
            type="button"
            class="seg-btn"
            :class="{ active: scope === s }"
            role="tab"
            :aria-selected="scope === s"
            @click="scope = s"
          >
            {{ anchorageScopeLabel(s) }}
          </button>
        </div>

        <div class="matrix">
          <!-- 三张档位表走行编辑器:区间列与汇总由组件算,单位换算与校验留在本文件。
               只渲染当前这一侧 —— 锚地内外两套输入各自保留在 draft 里 -->
          <template v-if="scope === 'inside'">
            <TierRowsEditor
              :ref="(el) => setEditor('insideWeight', el)"
              title="重量档"
              bound-unit="kg"
              amount-unit="元"
              :rows="draft.insideWeight"
              :errors="tierErrors.insideWeight"
              hint="超过最高档的单会被拒单,不会按最上一档收"
              @add="addTier('insideWeight')"
              @remove="(i) => removeTier('insideWeight', i)"
              @change="(i, f, v) => onTierChange('insideWeight', i, f, v)"
              @blur="() => onTierBlur('insideWeight')"
            />
            <TierRowsEditor
              :ref="(el) => setEditor('insideDistance', el)"
              title="距离档"
              bound-unit="公里"
              amount-unit="元"
              :rows="draft.insideDistance"
              :errors="tierErrors.insideDistance"
              hint="距离按大圆距离向上取整后比上界;超过最高档的单会被拒单"
              @add="addTier('insideDistance')"
              @remove="(i) => removeTier('insideDistance', i)"
              @change="(i, f, v) => onTierChange('insideDistance', i, f, v)"
              @blur="() => onTierBlur('insideDistance')"
            />
            <!-- 第三维与超长加价是契约必填:画板只画了两维,简化不是删功能的许可 -->
            <TierRowsEditor
              :ref="(el) => setEditor('insideVolume', el)"
              title="体积档"
              bound-unit="m³"
              amount-unit="元"
              :rows="draft.insideVolume"
              :errors="tierErrors.insideVolume"
              hint="默认一档盖住所有单、金额 0:与只有重量 × 距离时完全相同"
              @add="addTier('insideVolume')"
              @remove="(i) => removeTier('insideVolume', i)"
              @change="(i, f, v) => onTierChange('insideVolume', i, f, v)"
              @blur="() => onTierBlur('insideVolume')"
            />
          </template>
          <template v-else>
            <TierRowsEditor
              :ref="(el) => setEditor('outsideWeight', el)"
              title="重量档"
              bound-unit="kg"
              amount-unit="元"
              :rows="draft.outsideWeight"
              :errors="tierErrors.outsideWeight"
              hint="超过最高档的单会被拒单,不会按最上一档收"
              @add="addTier('outsideWeight')"
              @remove="(i) => removeTier('outsideWeight', i)"
              @change="(i, f, v) => onTierChange('outsideWeight', i, f, v)"
              @blur="() => onTierBlur('outsideWeight')"
            />
            <TierRowsEditor
              :ref="(el) => setEditor('outsideDistance', el)"
              title="距离档"
              bound-unit="公里"
              amount-unit="元"
              :rows="draft.outsideDistance"
              :errors="tierErrors.outsideDistance"
              hint="距离按大圆距离向上取整后比上界;超过最高档的单会被拒单"
              @add="addTier('outsideDistance')"
              @remove="(i) => removeTier('outsideDistance', i)"
              @change="(i, f, v) => onTierChange('outsideDistance', i, f, v)"
              @blur="() => onTierBlur('outsideDistance')"
            />
            <TierRowsEditor
              :ref="(el) => setEditor('outsideVolume', el)"
              title="体积档"
              bound-unit="m³"
              amount-unit="元"
              :rows="draft.outsideVolume"
              :errors="tierErrors.outsideVolume"
              hint="默认一档盖住所有单、金额 0:与只有重量 × 距离时完全相同"
              @add="addTier('outsideVolume')"
              @remove="(i) => removeTier('outsideVolume', i)"
              @change="(i, f, v) => onTierChange('outsideVolume', i, f, v)"
              @blur="() => onTierBlur('outsideVolume')"
            />
          </template>
          <div class="matrix-field">
            <span class="field-label">超长规格加价(元)</span>
            <n-input v-if="scope === 'inside'" v-model:value="draft.insideOversized" />
            <n-input v-else v-model:value="draft.outsideOversized" />
            <span class="hint-text">按体积分档的真表把这里填 0,两条路只走一条</span>
          </div>
        </div>

        <n-space align="center" class="row geofence">
          <span class="field-label">锚地范围（圆心 + 半径公里）</span>
          <n-input-number v-model:value="draft.centerLat" :precision="6" class="geo-input" />
          <n-input-number v-model:value="draft.centerLng" :precision="6" class="geo-input" />
          <n-input v-model:value="draft.radiusKm" class="radius-input">
            <template #suffix>公里</template>
          </n-input>
        </n-space>
        <p class="hint-text">起终点任一落在圆外,整单按锚地外计价(AS-009:真边界是海图上的多边形)</p>
      </PanelCard>

      <!-- 右栏:版本列表 + 发布。行内状态词与颜色都来自 labels.ts 的单点表 -->
      <PanelCard :tone="TONE" title="版本" subtitle="发布中的那一版决定所有新单的价;历史单永远按成交时的快照结算。" :notes="LIST_NOTES">
        <div v-if="tables.length === 0" class="hint-text">还没有任何计价表 —— 先在左边新建一版草稿</div>
        <div v-else class="versions">
          <button
            v-for="t in tables"
            :key="t.version"
            type="button"
            class="version-row"
            :class="{ active: t.version === selectedVersion }"
            @click="select(t)"
          >
            <span class="ver-main">
              <span class="ver-name">{{ t.version }}</span>
              <span class="ver-times">
                <span>建 {{ formatShanghaiDateTime(t.createdAt) }}</span>
                <span v-if="t.publishedAt !== null">发布 {{ formatShanghaiDateTime(t.publishedAt) }}</span>
              </span>
            </span>
            <StatusTag
              :status="t.status"
              :label="pricingTableStatusLabel(t.status)"
              :group="pricingTableStatusGroup(t.status)"
              :tone="TONE"
              size="sm"
            />
          </button>
        </div>

        <!-- 主按钮 = 发布:整宽、品牌蓝、圆角比卡片键大一号(颜色走主题的主按钮档,不写裸色值) -->
        <n-button
          class="publish"
          type="primary"
          size="large"
          block
          :disabled="publishTarget === null"
          @click="publishSelected"
        >
          <template v-if="publishTarget !== null">发布 {{ publishTarget.version }}</template>
          <template v-else>发布</template>
        </n-button>
        <p class="hint-text">
          点上面任一行选中它,按钮就是发它;只有草稿能发布,同一时刻只有一版发布中。
        </p>
      </PanelCard>
    </div>
  </div>
</template>

<style scoped>
/* 页头与各块之间的纵向节奏:画板是 16 —— 这里由 .page 的 gap 一处给 */
.page {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* 页头右端那句说明(画板:12px 三级灰,排在刷新键左边) */
.header-note {
  font-size: 12px;
  color: var(--sd-text3);
}

/* 刷新:画板的幽灵键(无底、1px 描边 border-strong、内边距 [8,14]、圆角 sm;本板无图标) */
.ghost {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: 1px solid var(--sd-border-strong);
  border-radius: var(--sd-radius-sm);
  background: transparent;
  color: var(--sd-text2);
  font: inherit;
  font-size: 13px;
  cursor: pointer;
}

.ghost:hover {
  color: var(--sd-text1);
  border-color: var(--sd-brand);
}

/* 画板的双栏:左档位编辑、右版本;窄屏落成一条 */
.panels {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 16px;
  align-items: start;
}

@media (max-width: 1100px) {
  .panels {
    grid-template-columns: 1fr;
  }
}

.row {
  margin-bottom: 12px;
}

.field-label {
  font-size: 13px;
  color: var(--sd-text2);
}

.hint-text {
  font-size: 12px;
  color: var(--sd-text3);
}

.version-input {
  width: 200px;
}

/* 分段控件:画板是深色轨道上浮一块选中的面板 */
.segment {
  display: flex;
  padding: 4px;
  margin-bottom: 12px;
  border-radius: var(--sd-radius-md);
  background: var(--sd-bg);
}

.seg-btn {
  flex: 1 1 0;
  padding: 8px 0;
  border: none;
  border-radius: var(--sd-radius-sm);
  background: transparent;
  color: var(--sd-text2);
  font: inherit;
  font-size: 13px;
  cursor: pointer;
}

.seg-btn.active {
  background: var(--sd-surface);
  color: var(--sd-text1);
  font-weight: 600;
}

.matrix {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.matrix-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.geofence {
  margin-top: 16px;
  margin-bottom: 8px;
}

.geo-input {
  width: 140px;
}

.radius-input {
  width: 110px;
}

/* ── 右栏:版本行与发布键 ─────────────────────────────── */

.versions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.version-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  padding: 10px 12px;
  border: 1px solid transparent;
  border-radius: var(--sd-radius-md);
  background: var(--sd-bg);
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.version-row.active {
  border-color: var(--sd-brand);
}

.ver-main {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.ver-name {
  font-family: var(--sd-font-mono);
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  color: var(--sd-text1);
}

.ver-times {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  color: var(--sd-text3);
}

/* 主按钮 = 发布:整宽、圆角比卡片键大一号(底色与白字走主题的主按钮档) */
.publish {
  margin-top: 12px;
  border-radius: var(--sd-radius-xl);
  font-size: 15px;
  font-weight: 600;
}
</style>
